import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  Car,
  Check,
  FileText,
  Loader2,
  Paperclip,
  Plus,
  Receipt,
  Send,
  Trash2,
  UtensilsCrossed,
  X,
} from 'lucide-react'
import { api, apiErrorMessage } from '../lib/api'
import { confirmDialog, promptDialog, toast } from '../lib/dialogs'
import Modal from '../components/ui/Modal'
import { useAuth } from '../contexts/AuthContext'
import { formatCents, parseEurosToCents, parseQuantity, centsToEurosInput } from '../lib/money'
import { useExpenseCategories } from '../lib/expense-categories'
import { useServerTableSort } from '../lib/useTableSort'
import type {
  AllowanceRatesDto,
  ExpenseSheetDto,
  ExpenseSheetLineDto,
  ExpenseSheetLineKindT,
  ExpenseSheetListResponse,
  ExpenseSheetStatusT,
  UserListItem,
} from '@jr2os/shared'

/**
 * HOJA DE GASTOS — lo que una persona adelanta a lo largo de un mes.
 *
 * La pantalla previsualiza los importes, pero NO los decide: el servidor los
 * recalcula al guardar con la tarifa y el límite legal del día del gasto. Lo que
 * se ve aquí es una ayuda para teclear, no la fuente de verdad.
 */

const STATUS_PILL: Record<ExpenseSheetStatusT, string> = {
  BORRADOR: 'bg-slate-500/15 t-secondary border-slate-500/30',
  ENVIADA: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  APROBANDO: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40',
  APROBADA: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  RECHAZADA: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40',
}

const KIND_ICON: Record<ExpenseSheetLineKindT, typeof Receipt> = {
  TIQUE: Receipt,
  KILOMETRAJE: Car,
  DIETA: UtensilsCrossed,
}

const CURRENT_YEAR = new Date().getFullYear()
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2]

/** Línea en edición. Los importes viven como texto mientras se teclea. */
interface DraftLine {
  key: string
  /** id de la línea en el servidor; ausente en una línea nueva. */
  id?: string
  kind: ExpenseSheetLineKindT
  lineDate: string
  concept: string
  category: string
  opportunityId: string | null
  supplierName: string
  supplierNif: string
  invoiceNumber: string
  vatRows: { rate: string; base: string }[]
  km: string
  originPlace: string
  destinationPlace: string
  vehiclePlate: string
  days: string
  overnight: boolean
  abroad: boolean
  documents: { id: string; fileName: string; latestVersion: number }[]
}

let draftSeq = 0
const newKey = () => `d${++draftSeq}`

function emptyLine(kind: ExpenseSheetLineKindT, category: string, date: string): DraftLine {
  return {
    key: newKey(),
    kind,
    lineDate: date,
    concept: '',
    category,
    opportunityId: null,
    supplierName: '',
    supplierNif: '',
    invoiceNumber: '',
    vatRows: kind === 'TIQUE' ? [{ rate: '21', base: '' }] : [],
    km: '',
    originPlace: '',
    destinationPlace: '',
    vehiclePlate: '',
    days: '1',
    overnight: false,
    abroad: false,
    documents: [],
  }
}

function toDraft(l: ExpenseSheetLineDto): DraftLine {
  return {
    key: newKey(),
    id: l.id,
    kind: l.kind,
    lineDate: l.lineDate,
    concept: l.concept,
    category: l.category,
    opportunityId: l.opportunityId,
    supplierName: l.supplierName ?? '',
    supplierNif: l.supplierNif ?? '',
    invoiceNumber: l.invoiceNumber ?? '',
    vatRows: l.vatLines.map((v) => ({
      rate: String(Math.round(v.vatRate * 100)),
      base: centsToEurosInput(v.subtotalCents),
    })),
    km: l.km == null ? '' : String(l.km),
    originPlace: l.originPlace ?? '',
    destinationPlace: l.destinationPlace ?? '',
    vehiclePlate: l.vehiclePlate ?? '',
    days: l.days == null ? '' : String(l.days),
    overnight: l.overnight,
    abroad: l.abroad,
    documents: l.documents,
  }
}

/** Previsualización del importe. El servidor manda; esto solo evita teclear a ciegas. */
function previewCents(l: DraftLine, rates: AllowanceRatesDto | null): number {
  if (l.kind === 'TIQUE') {
    return l.vatRows.reduce((acc, r) => {
      const base = parseEurosToCents(r.base || '0')
      const rate = Number(r.rate || '0') / 100
      return acc + base + Math.round(base * rate)
    }, 0)
  }
  if (!rates) return 0
  if (l.kind === 'KILOMETRAJE') {
    return Math.round(parseQuantity(l.km) * rates.mileageRateCents)
  }
  const perDiem = l.abroad
    ? l.overnight
      ? rates.perDiem.abroadOvernightCents
      : rates.perDiem.abroadCents
    : l.overnight
      ? rates.perDiem.nationalOvernightCents
      : rates.perDiem.nationalCents
  return Math.round(parseQuantity(l.days) * perDiem)
}

export default function ExpenseSheets() {
  const { t } = useTranslation('expenseSheets')
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'ADMIN'
  const myId = auth.status === 'authenticated' ? auth.user.id : ''
  const canWrite =
    auth.status === 'authenticated' &&
    (auth.user.role === 'ADMIN' || auth.user.role === 'CONSULTOR')

  const [items, setItems] = useState<ExpenseSheetDto[]>([])
  // Cuántas hojas hay en total con estos filtros (la API lo devuelve): la pantalla pide 100 sin paginador, así que si hay más se avisa.
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [year, setYear] = useState<number>(CURRENT_YEAR)
  const [status, setStatus] = useState<ExpenseSheetStatusT | ''>('')
  const [userFilter, setUserFilter] = useState('')
  const [users, setUsers] = useState<UserListItem[]>([])
  const [rates, setRates] = useState<AllowanceRatesDto | null>(null)
  const [open, setOpen] = useState<ExpenseSheetDto | null>(null)
  const [creating, setCreating] = useState(false)
  // Orden por columna: lo hace el servidor (la lista la pagina él); aquí solo se guarda en la URL y se pide. Es un hook, así que
  // va por encima del ternario cargando / vacío / tabla. No hay paginador ni número de página al que volver al cambiar de orden.
  const { sort, header } = useServerTableSort({ persist: 'url' })

  // Respuestas atrasadas: cada carga lleva su número y solo la última puede escribir en el estado. Si el usuario cambia de orden o
  // de filtro antes de que llegue la respuesta anterior, esa no pisa a la nueva ni apaga el indicador de carga de la que sigue en vuelo.
  const reqId = useRef(0)

  const reload = useCallback(async () => {
    const my = ++reqId.current
    setLoading(true)
    setError(null)
    try {
      const p = new URLSearchParams({ year: String(year), pageSize: '100' })
      if (status) p.set('status', status)
      if (userFilter) p.set('userId', userFilter)
      if (sort.key) {
        p.set('sort', sort.key)
        p.set('dir', sort.dir)
      }
      const data = await api.get<ExpenseSheetListResponse>(`/expense-sheets?${p.toString()}`)
      if (my === reqId.current) {
        setItems(data.items)
        setTotal(data.total)
      }
    } catch (err) {
      if (my === reqId.current) setError(apiErrorMessage(err, t('errors.loadFailed')))
    } finally {
      if (my === reqId.current) setLoading(false)
    }
  }, [year, status, userFilter, sort.key, sort.dir, t])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    void api
      .get<AllowanceRatesDto>('/expense-sheets/rates')
      .then(setRates)
      .catch(() => setRates(null))
    if (isAdmin) {
      void api
        .get<{ users: UserListItem[] }>('/users?pageSize=200')
        .then((r) => setUsers(r.users.filter((u) => u.isActive)))
        .catch(() => setUsers([]))
    }
  }, [isAdmin])

  const openFresh = useCallback(
    async (id: string) => {
      try {
        const r = await api.get<{ sheet: ExpenseSheetDto }>(`/expense-sheets/${id}`)
        setOpen(r.sheet)
      } catch (e) {
        // Una fila que no abre y no dice nada parece la aplicación colgada.
        toast.error(apiErrorMessage(e, t('errors.loadFailed')))
      }
    },
    [t]
  )

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <FileText size={20} className="text-amber-700 dark:text-amber-300" />
            {t('title')}
          </h1>
          <p className="text-xs t-muted">{t('subtitle')}</p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-amber-600 via-amber-500 to-amber-700 hover:shadow-glow-amber flex items-center gap-1.5"
          >
            <Plus size={14} /> {t('newButton')}
          </button>
        )}
      </header>

      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <label className="text-xs t-muted">
          {t('filters.year')}
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="mt-1 block bg-transparent border border-base rounded-lg px-2 py-1.5 text-sm t-primary"
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y} className="bg-slate-900">
                {y}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs t-muted">
          {t('filters.status')}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as ExpenseSheetStatusT | '')}
            className="mt-1 block bg-transparent border border-base rounded-lg px-2 py-1.5 text-sm t-primary"
          >
            <option value="" className="bg-slate-900">
              {t('filters.all')}
            </option>
            {(['BORRADOR', 'ENVIADA', 'APROBANDO', 'APROBADA', 'RECHAZADA'] as const).map((s) => (
              <option key={s} value={s} className="bg-slate-900">
                {t(`status.${s}`)}
              </option>
            ))}
          </select>
        </label>
        {isAdmin && (
          <label className="text-xs t-muted">
            {t('filters.person')}
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="mt-1 block bg-transparent border border-base rounded-lg px-2 py-1.5 text-sm t-primary"
            >
              <option value="" className="bg-slate-900">
                {t('filters.everyone')}
              </option>
              {users.map((u) => (
                <option key={u.id} value={u.id} className="bg-slate-900">
                  {u.fullName}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {error && (
        <div className="surface p-3 text-sm text-rose-600 dark:text-rose-300 flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      <div className="surface overflow-x-auto">
        {/* El spinner solo si aún no hay filas. Al recargar con filas ya pintadas (cambio de orden, de filtro) la tabla se queda,
            atenuada y sin clics: si se sustituyera entera, el botón de la cabecera recién pulsado se desmontaría y se perdería
            el foco y la posición del scroll. */}
        {loading && items.length === 0 ? (
          <div className="p-8 grid place-items-center">
            <Loader2 className="animate-spin t-muted" />
          </div>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-sm t-muted">{t('empty')}</p>
        ) : (
          <table
            className={`w-full text-sm transition-opacity ${loading ? 'opacity-60 pointer-events-none' : ''}`}
            aria-busy={loading}
          >
            <thead className="sticky top-0 bg-surface">
              <tr className="text-left text-xs t-muted border-b border-base">
                {header('ref', t('cols.ref'), { caps: false })}
                {header('period', t('cols.period'), { caps: false })}
                {header('person', t('cols.person'), { caps: false })}
                {header('lines', t('cols.lines'), { align: 'right', caps: false })}
                {/* Total y pendiente se SUMAN al montar cada hoja (líneas y pagadores de sus gastos): la base de datos no
                    los ordena, y ordenar solo la página visible sería engañoso. Sin control de orden. */}
                <th className="px-3 py-2 text-right">{t('cols.total')}</th>
                <th className="px-3 py-2 text-right">{t('cols.pending')}</th>
                {header('status', t('cols.status'), { caps: false })}
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => void openFresh(s.id)}
                  className="border-b border-base/50 hover:bg-white/5 cursor-pointer"
                >
                  <td className="px-3 py-2 font-mono text-xs t-secondary">{s.ref}</td>
                  <td className="px-3 py-2 t-secondary">
                    {t(`months.${s.month}`)} {s.year}
                  </td>
                  <td className="px-3 py-2 t-primary">{s.userName}</td>
                  <td className="px-3 py-2 text-right t-secondary">{s.lines.length}</td>
                  <td className="px-3 py-2 text-right t-primary font-medium">
                    {formatCents(s.totalCents)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {s.pendingReimbursementCents > 0 ? (
                      <span className="text-orange-600 dark:text-orange-300">
                        {formatCents(s.pendingReimbursementCents)}
                      </span>
                    ) : (
                      <span className="t-faint">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`px-2 py-0.5 rounded-full border text-[11px] ${STATUS_PILL[s.status]}`}
                    >
                      {t(`status.${s.status}`)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* La lista pide 100 hojas y no tiene paginador: si hay más con estos filtros, que se sepa que no se están viendo todas. */}
      {total > items.length && (
        <p className="text-xs t-muted px-1">{t('windowNotice', { shown: items.length, total })}</p>
      )}

      {creating && (
        <CreateSheetModal
          isAdmin={isAdmin}
          users={users}
          myId={myId}
          onClose={() => setCreating(false)}
          onCreated={async (id) => {
            setCreating(false)
            await reload()
            await openFresh(id)
          }}
        />
      )}

      {open && (
        <SheetDetail
          sheet={open}
          rates={rates}
          isAdmin={isAdmin}
          myId={myId}
          onClose={() => setOpen(null)}
          onChanged={async (s) => {
            setOpen(s)
            await reload()
          }}
          onGone={async () => {
            setOpen(null)
            await reload()
          }}
        />
      )}
    </div>
  )
}

function CreateSheetModal({
  isAdmin,
  users,
  myId,
  onClose,
  onCreated,
}: {
  isAdmin: boolean
  users: UserListItem[]
  myId: string
  onClose: () => void
  onCreated: (id: string) => void | Promise<void>
}) {
  const { t } = useTranslation('expenseSheets')
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [userId, setUserId] = useState(myId)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  return (
    <Modal open onClose={onClose} title={t('create.title')} description={t('create.description')}>
      <form
        className="space-y-3"
        onSubmit={async (e) => {
          e.preventDefault()
          setSaving(true)
          setErr(null)
          try {
            const r = await api.post<{ sheet: ExpenseSheetDto }>('/expense-sheets', {
              year,
              month,
              ...(isAdmin && userId !== myId ? { userId } : {}),
            })
            await onCreated(r.sheet.id)
          } catch (e2) {
            setErr(apiErrorMessage(e2, t('errors.saveFailed')))
          } finally {
            setSaving(false)
          }
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs t-muted">
            {t('create.month')}
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="mt-1 w-full bg-transparent border border-base rounded-lg px-2 py-1.5 text-sm t-primary"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m} className="bg-slate-900">
                  {t(`months.${m}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs t-muted">
            {t('create.year')}
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="mt-1 w-full bg-transparent border border-base rounded-lg px-2 py-1.5 text-sm t-primary"
            >
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y} className="bg-slate-900">
                  {y}
                </option>
              ))}
            </select>
          </label>
        </div>
        {isAdmin && users.length > 0 && (
          <label className="text-xs t-muted block">
            {t('create.person')}
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="mt-1 w-full bg-transparent border border-base rounded-lg px-2 py-1.5 text-sm t-primary"
            >
              {users.map((u) => (
                <option key={u.id} value={u.id} className="bg-slate-900">
                  {u.fullName}
                </option>
              ))}
            </select>
          </label>
        )}
        {err && <p className="text-xs text-rose-500">{err}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm t-secondary">
            {t('actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-amber-600 to-amber-700 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : t('create.submit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function SheetDetail({
  sheet,
  rates,
  isAdmin,
  myId,
  onClose,
  onChanged,
  onGone,
}: {
  sheet: ExpenseSheetDto
  rates: AllowanceRatesDto | null
  isAdmin: boolean
  myId: string
  onClose: () => void
  onChanged: (s: ExpenseSheetDto) => void | Promise<void>
  onGone: () => void | Promise<void>
}) {
  const { t } = useTranslation('expenseSheets')
  const { categories, defaultCode, nameOf } = useExpenseCategories()
  // Un kilometraje o una dieta son, por definición, gasto de viaje: arrancar en
  // "Otros" obliga a corregir la tipología en todas las líneas y, si a alguien
  // se le olvida, el desglose de la Cuenta de Resultados deja de decir nada.
  const travelCode = categories.find((c) => c.code === 'VIAJES_DIETAS')?.code ?? defaultCode
  const editable = sheet.status === 'BORRADOR' || sheet.status === 'RECHAZADA'
  const [lines, setLines] = useState<DraftLine[]>(() => sheet.lines.map(toDraft))
  const [notes, setNotes] = useState(sheet.notes ?? '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    setLines(sheet.lines.map(toDraft))
    setNotes(sheet.notes ?? '')
  }, [sheet])

  const mm = String(sheet.month).padStart(2, '0')
  const defaultDate = `${sheet.year}-${mm}-01`
  // La fecha de la línea decide el ejercicio del asiento, así que se acota al
  // mes de la hoja: una errata de un dígito en el año metía el gasto en una
  // contabilidad ya presentada.
  const period = {
    min: defaultDate,
    max: new Date(Date.UTC(sheet.year, sheet.month, 0)).toISOString().slice(0, 10),
  }
  const previewTotal = useMemo(
    () => lines.reduce((a, l) => a + previewCents(l, rates), 0),
    [lines, rates]
  )

  const patch = (key: string, up: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...up } : l)))

  async function save(): Promise<ExpenseSheetDto | null> {
    setSaving(true)
    setErr(null)
    try {
      const payload = {
        notes: notes.trim() || null,
        lines: lines.map((l) => ({
          kind: l.kind,
          lineDate: l.lineDate,
          concept: l.concept.trim(),
          category: l.category,
          opportunityId: l.opportunityId,
          ...(l.kind === 'TIQUE'
            ? {
                supplierName: l.supplierName.trim(),
                supplierNif: l.supplierNif.trim() || null,
                invoiceNumber: l.invoiceNumber.trim() || null,
                vatLines: l.vatRows.map((r) => ({
                  vatRate: Number(r.rate || '0') / 100,
                  subtotalCents: parseEurosToCents(r.base || '0'),
                })),
              }
            : {}),
          ...(l.kind === 'KILOMETRAJE'
            ? {
                km: parseQuantity(l.km),
                originPlace: l.originPlace.trim(),
                destinationPlace: l.destinationPlace.trim(),
                vehiclePlate: l.vehiclePlate.trim() || null,
              }
            : {}),
          ...(l.kind === 'DIETA'
            ? {
                days: parseQuantity(l.days),
                overnight: l.overnight,
                abroad: l.abroad,
              }
            : {}),
          documentIds: l.documents.map((d) => d.id),
        })),
      }
      const r = await api.patch<{ sheet: ExpenseSheetDto }>(`/expense-sheets/${sheet.id}`, payload)
      await onChanged(r.sheet)
      return r.sheet
    } catch (e) {
      setErr(apiErrorMessage(e, t('errors.saveFailed')))
      return null
    } finally {
      setSaving(false)
    }
  }

  /**
   * Devuelve si la acción salió bien. Antes no devolvía nada y el llamante
   * enseñaba el toast verde de "aprobada y contabilizada" pasara lo que pasara:
   * el texto del error quedaba dentro del modal, por encima del listado con
   * scroll, mientras un toast de cuatro segundos decía lo contrario.
   */
  async function act(path: string, body?: unknown): Promise<boolean> {
    setSaving(true)
    setErr(null)
    try {
      const r = await api.post<{ sheet: ExpenseSheetDto }>(`/expense-sheets/${sheet.id}/${path}`, body)
      await onChanged(r.sheet)
      return true
    } catch (e) {
      const msg = apiErrorMessage(e, t('errors.saveFailed'))
      setErr(msg)
      toast.error(msg)
      return false
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      maxWidth="max-w-5xl"
      title={`${t('detail.title')} ${sheet.ref} · ${t(`months.${sheet.month}`)} ${sheet.year}`}
      description={sheet.userName}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <span className={`px-2 py-0.5 rounded-full border text-[11px] ${STATUS_PILL[sheet.status]}`}>
            {t(`status.${sheet.status}`)}
          </span>
          <div className="flex items-center gap-4 text-sm">
            <span className="t-muted">
              {t('detail.total')}{' '}
              <strong className="t-primary">
                {formatCents(editable ? previewTotal : sheet.totalCents)}
              </strong>
            </span>
            {sheet.exemptCents > 0 && (
              <span className="t-muted">
                {t('detail.exempt')}{' '}
                <strong className="text-emerald-600 dark:text-emerald-300">
                  {formatCents(sheet.exemptCents)}
                </strong>
              </span>
            )}
            {sheet.pendingReimbursementCents > 0 && (
              <span className="t-muted">
                {t('detail.pending')}{' '}
                <strong className="text-orange-600 dark:text-orange-300">
                  {formatCents(sheet.pendingReimbursementCents)}
                </strong>
              </span>
            )}
          </div>
        </div>

        {sheet.status === 'APROBANDO' && (
          <p className="text-sm text-sky-700 dark:text-sky-300 flex items-start gap-2 surface p-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            {t('detail.stuckApproving')}
          </p>
        )}
        {sheet.status === 'RECHAZADA' && sheet.rejectedReason && (
          <p className="text-sm text-rose-600 dark:text-rose-300 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            {t('detail.rejectedBecause', { reason: sheet.rejectedReason })}
          </p>
        )}
        {sheet.status === 'APROBADA' && (
          <p className="text-xs t-muted">
            {t('detail.approvedBy', {
              name: sheet.approvedByName ?? '—',
              date: sheet.approvedAt ? new Date(sheet.approvedAt).toLocaleDateString() : '',
            })}
          </p>
        )}

        {sheet.taxableCents > 0 && (
          <p className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2 surface p-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            {t('detail.taxableWarning', { amount: formatCents(sheet.taxableCents) })}
          </p>
        )}

        <div className="space-y-3 max-h-[46vh] overflow-y-auto pr-1">
          {lines.length === 0 && (
            <p className="text-sm t-muted text-center py-6">{t('detail.noLines')}</p>
          )}
          {lines.map((l, i) => (
            <LineCard
              key={l.key}
              line={l}
              index={i}
              dto={l.id ? sheet.lines.find((x) => x.id === l.id) : undefined}
              editable={editable}
              period={period}
              rates={rates}
              categories={categories.map((c) => ({ code: c.code, name: c.name }))}
              nameOf={nameOf}
              onPatch={(up) => patch(l.key, up)}
              onRemove={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
            />
          ))}
        </div>

        {editable && (
          <div className="flex flex-wrap gap-2">
            {(['TIQUE', 'KILOMETRAJE', 'DIETA'] as const).map((k) => {
              const Icon = KIND_ICON[k]
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() =>
                    setLines((prev) => [
                      ...prev,
                      emptyLine(k, k === 'TIQUE' ? defaultCode : travelCode, defaultDate),
                    ])
                  }
                  className="px-3 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-white/5 flex items-center gap-1.5"
                >
                  <Icon size={13} /> {t(`kinds.add.${k}`)}
                </button>
              )
            })}
          </div>
        )}

        {editable && (
          <label className="text-xs t-muted block">
            {t('detail.notes')}
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full bg-transparent border border-base rounded-lg px-2 py-1.5 text-sm t-primary"
            />
          </label>
        )}

        {err && <p className="text-sm text-rose-500">{err}</p>}

        <div className="flex flex-wrap justify-end gap-2 border-t border-base pt-3">
          {editable && (
            <>
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  if (!(await confirmDialog({ title: t('confirm.deleteTitle'), message: t('confirm.deleteBody'), tone: 'danger' }))) return
                  try {
                    await api.delete(`/expense-sheets/${sheet.id}`)
                    await onGone()
                  } catch (e) {
                    // El servidor se niega, por ejemplo, si la hoja tiene líneas
                    // ya contabilizadas. Sin esto el botón no hacía nada y no
                    // decía por qué.
                    const msg = apiErrorMessage(e, t('errors.saveFailed'))
                    setErr(msg)
                    toast.error(msg)
                  }
                }}
                className="px-3 py-1.5 rounded-lg text-sm text-rose-600 dark:text-rose-300 hover:bg-rose-500/10 flex items-center gap-1.5"
              >
                <Trash2 size={14} /> {t('actions.delete')}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void save()}
                className="px-3 py-1.5 rounded-lg text-sm border border-base t-secondary hover:bg-white/5"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : t('actions.save')}
              </button>
              <button
                type="button"
                disabled={saving || lines.length === 0}
                onClick={async () => {
                  // Guardar SIEMPRE antes de enviar: si no, se enviaría a
                  // aprobación una versión distinta de la que se está viendo.
                  const saved = await save()
                  if (saved) await act('submit')
                }}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-amber-600 to-amber-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Send size={14} /> {t('actions.submit')}
              </button>
            </>
          )}

          {(sheet.status === 'ENVIADA' || sheet.status === 'APROBANDO') &&
            isAdmin &&
            sheet.userId !== myId && (
            <>
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  const reason = await promptDialog({
                    title: t('confirm.rejectTitle'),
                    message: t('confirm.rejectBody'),
                    placeholder: t('confirm.rejectPlaceholder'),
                  })
                  if (reason && reason.trim()) await act('reject', { reason: reason.trim() })
                }}
                className="px-3 py-1.5 rounded-lg text-sm text-rose-600 dark:text-rose-300 hover:bg-rose-500/10 flex items-center gap-1.5"
              >
                <X size={14} /> {t('actions.reject')}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  if (
                    !(await confirmDialog({
                      title: t('confirm.approveTitle'),
                      message: t('confirm.approveBody', { amount: formatCents(sheet.totalCents) }),
                    }))
                  )
                    return
                  if (await act('approve')) toast.success(t('toasts.approved'))
                }}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Check size={14} /> {t('actions.approve')}
              </button>
            </>
          )}

          {(sheet.status === 'ENVIADA' || sheet.status === 'APROBANDO') &&
            (!isAdmin || sheet.userId === myId) && (
            <p className="text-xs t-muted self-center">
              {sheet.userId === myId ? t('detail.waitingOther') : t('detail.waitingAdmin')}
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}

function LineCard({
  line,
  index,
  dto,
  editable: sheetEditable,
  period,
  rates,
  categories,
  nameOf,
  onPatch,
  onRemove,
}: {
  line: DraftLine
  index: number
  dto: ExpenseSheetLineDto | undefined
  editable: boolean
  period: { min: string; max: string } | null
  rates: AllowanceRatesDto | null
  categories: { code: string; name: string }[]
  nameOf: (code: string | null | undefined) => string
  onPatch: (up: Partial<DraftLine>) => void
  onRemove: () => void
}) {
  const { t } = useTranslation('expenseSheets')
  const Icon = KIND_ICON[line.kind]
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const preview = previewCents(line, rates)
  // Una línea ya contabilizada tiene un gasto y un asiento detrás: se enseña,
  // no se toca. Corregirla es anular su gasto, no editar el texto de la hoja —
  // y en cuanto se anula (contra-asiento emitido) vuelve a estar editable, que
  // es justo la salida que el mensaje de error propone.
  const contabilizada = !!dto?.expenseRef && !dto.expenseVoided
  const editable = sheetEditable && !contabilizada
  const input =
    'w-full bg-transparent border border-base rounded-lg px-2 py-1.5 text-sm t-primary disabled:opacity-70'

  async function upload(file: File) {
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('category', 'JUSTIFICANTE_GASTO')
      const r = await api.upload<{
        document: { id: string; fileName: string; latestVersion: number }
      }>('/documents', form)
      onPatch({
        documents: [
          ...line.documents,
          { id: r.document.id, fileName: r.document.fileName, latestVersion: r.document.latestVersion },
        ],
      })
    } catch (e) {
      toast.error(apiErrorMessage(e, t('errors.uploadFailed')))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="surface p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Icon size={15} className="text-amber-600 dark:text-amber-300 shrink-0" />
        <span className="text-xs font-semibold t-secondary">{t(`kinds.${line.kind}`)}</span>
        <span className="ml-auto text-sm font-medium t-primary">
          {formatCents(editable ? preview : (dto?.totalCents ?? preview))}
        </span>
        {dto?.expenseRef && (
          <span
            className={`text-[11px] font-mono ${dto.expenseVoided ? 't-faint line-through' : 't-muted'}`}
            title={dto.expenseVoided ? t('line.expenseVoided') : undefined}
          >
            {t('line.expense', { ref: dto.expenseRef })}
          </span>
        )}
        {editable && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={t('actions.removeLine')}
            className="t-faint hover:text-rose-500"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <label className="text-[11px] t-muted">
          {t('line.date')}
          <input
            type="date"
            value={line.lineDate}
            min={period?.min}
            max={period?.max}
            disabled={!editable}
            onChange={(e) => onPatch({ lineDate: e.target.value })}
            className={`mt-0.5 ${input}`}
          />
        </label>
        <label className="text-[11px] t-muted sm:col-span-2">
          {t('line.concept')}
          <input
            value={line.concept}
            disabled={!editable}
            onChange={(e) => onPatch({ concept: e.target.value })}
            className={`mt-0.5 ${input}`}
          />
        </label>
        <label className="text-[11px] t-muted">
          {t('line.category')}
          {editable ? (
            <select
              value={line.category}
              onChange={(e) => onPatch({ category: e.target.value })}
              className={`mt-0.5 ${input}`}
            >
              {categories.map((c) => (
                <option key={c.code} value={c.code} className="bg-slate-900">
                  {c.name}
                </option>
              ))}
            </select>
          ) : (
            <p className="mt-0.5 text-sm t-primary py-1.5">{nameOf(line.category)}</p>
          )}
        </label>
      </div>

      {line.kind === 'TIQUE' && (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="text-[11px] t-muted">
              {t('line.supplier')}
              <input
                value={line.supplierName}
                disabled={!editable}
                onChange={(e) => onPatch({ supplierName: e.target.value })}
                className={`mt-0.5 ${input}`}
              />
            </label>
            <label className="text-[11px] t-muted">
              {t('line.nif')}
              <input
                value={line.supplierNif}
                disabled={!editable}
                onChange={(e) => onPatch({ supplierNif: e.target.value })}
                className={`mt-0.5 ${input}`}
              />
            </label>
            <label className="text-[11px] t-muted">
              {t('line.invoiceNumber')}
              <input
                value={line.invoiceNumber}
                disabled={!editable}
                onChange={(e) => onPatch({ invoiceNumber: e.target.value })}
                className={`mt-0.5 ${input}`}
              />
            </label>
          </div>
          {/* Un tique puede mezclar tipos: la comida al 10 % y el refresco al 21 %.
              Meterlo con un tipo medio deduce mal y descuadra el libro de IVA. */}
          {line.vatRows.map((r, i) => (
            <div key={i} className="flex items-end gap-2">
              <label className="text-[11px] t-muted w-24">
                {t('line.vatRate')}
                <select
                  value={r.rate}
                  disabled={!editable}
                  onChange={(e) =>
                    onPatch({
                      vatRows: line.vatRows.map((x, j) =>
                        j === i ? { ...x, rate: e.target.value } : x
                      ),
                    })
                  }
                  className={`mt-0.5 ${input}`}
                >
                  {['21', '10', '4', '0'].map((v) => (
                    <option key={v} value={v} className="bg-slate-900">
                      {v} %
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-[11px] t-muted flex-1">
                {t('line.base')}
                <input
                  inputMode="decimal"
                  value={r.base}
                  disabled={!editable}
                  onChange={(e) =>
                    onPatch({
                      vatRows: line.vatRows.map((x, j) =>
                        j === i ? { ...x, base: e.target.value } : x
                      ),
                    })
                  }
                  className={`mt-0.5 ${input}`}
                />
              </label>
              {editable && line.vatRows.length > 1 && (
                <button
                  type="button"
                  onClick={() => onPatch({ vatRows: line.vatRows.filter((_, j) => j !== i) })}
                  className="t-faint hover:text-rose-500 pb-2"
                  aria-label={t('actions.removeVatRow')}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
          {editable && (
            <button
              type="button"
              onClick={() => onPatch({ vatRows: [...line.vatRows, { rate: '10', base: '' }] })}
              className="text-[11px] t-muted hover:t-primary flex items-center gap-1"
            >
              <Plus size={12} /> {t('line.addVatRow')}
            </button>
          )}
        </div>
      )}

      {line.kind === 'KILOMETRAJE' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <label className="text-[11px] t-muted">
            {t('line.origin')}
            <input
              value={line.originPlace}
              disabled={!editable}
              onChange={(e) => onPatch({ originPlace: e.target.value })}
              className={`mt-0.5 ${input}`}
            />
          </label>
          <label className="text-[11px] t-muted">
            {t('line.destination')}
            <input
              value={line.destinationPlace}
              disabled={!editable}
              onChange={(e) => onPatch({ destinationPlace: e.target.value })}
              className={`mt-0.5 ${input}`}
            />
          </label>
          <label className="text-[11px] t-muted">
            {t('line.km')}
            <input
              inputMode="decimal"
              value={line.km}
              disabled={!editable}
              onChange={(e) => onPatch({ km: e.target.value })}
              className={`mt-0.5 ${input}`}
            />
          </label>
          <label className="text-[11px] t-muted">
            {t('line.plate')}
            <input
              value={line.vehiclePlate}
              disabled={!editable}
              onChange={(e) => onPatch({ vehiclePlate: e.target.value })}
              className={`mt-0.5 ${input}`}
            />
          </label>
          {rates && (
            <p className="col-span-2 sm:col-span-4 text-[11px] t-faint">
              {t('line.mileageHint', {
                rate: (rates.mileageRateCents / 100).toFixed(2),
              })}
            </p>
          )}
        </div>
      )}

      {line.kind === 'DIETA' && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-[11px] t-muted w-24">
            {t('line.days')}
            <input
              inputMode="decimal"
              value={line.days}
              disabled={!editable}
              onChange={(e) => onPatch({ days: e.target.value })}
              className={`mt-0.5 ${input}`}
            />
          </label>
          <label className="text-xs t-secondary flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={line.overnight}
              disabled={!editable}
              onChange={(e) => onPatch({ overnight: e.target.checked })}
            />
            {t('line.overnight')}
          </label>
          <label className="text-xs t-secondary flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={line.abroad}
              disabled={!editable}
              onChange={(e) => onPatch({ abroad: e.target.checked })}
            />
            {t('line.abroad')}
          </label>
          <p className="text-[11px] t-faint flex-1 min-w-[200px]">{t('line.perDiemHint')}</p>
        </div>
      )}

      {/* Justificantes: varios por línea (las dos caras de un tique, el parte de
          viaje junto al tique del depósito). */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {line.documents.map((d) => (
          <span
            key={d.id}
            className="text-[11px] t-secondary border border-base rounded-full px-2 py-0.5 flex items-center gap-1"
          >
            <Paperclip size={11} />
            <a
              href={`/api/documents/${d.id}/versions/${d.latestVersion}/download?inline=1`}
              target="_blank"
              rel="noreferrer"
              className="hover:underline max-w-[160px] truncate"
            >
              {d.fileName}
            </a>
            {editable && (
              <button
                type="button"
                onClick={() => onPatch({ documents: line.documents.filter((x) => x.id !== d.id) })}
                className="t-faint hover:text-rose-500"
                aria-label={t('actions.removeDocument')}
              >
                <X size={11} />
              </button>
            )}
          </span>
        ))}
        {editable && (
          <>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) void upload(f)
                e.target.value = ''
              }}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="text-[11px] t-muted hover:t-primary flex items-center gap-1"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
              {t('line.attach')}
            </button>
          </>
        )}
      </div>
      <span className="sr-only">{index}</span>
    </div>
  )
}
